/**
 * Text plumbing shared by the writing-analysis checkers: words and sentences with their
 * offsets in the original text, quoted passages, and whole-word phrase matching.
 *
 * Everything here reports character offsets into the text the reader pasted, so a
 * checker's findings can be drawn over the reader's own words by `HighlightedText`
 * without the text ever being rewritten. Nothing here knows English vocabulary; the
 * word lists live with the checker that owns them.
 */

/** A word and where it sits in the text. */
export interface Token {
  /** As written. */
  text: string;
  /** Lowercase, with curly apostrophes straightened. */
  lower: string;
  start: number;
  end: number;
}

/** A stretch of the text by offsets, end exclusive. */
export interface Span {
  start: number;
  end: number;
}

/** Letters with internal apostrophes. A hyphen ends a token: "decision-making" is two. */
const RE_LETTER_WORD = /\p{L}[\p{L}\p{M}]*(?:['’]\p{L}[\p{L}\p{M}]*)*/gu;

/**
 * A word as a word counter sees one: letters or digits, with hyphenated compounds and
 * contractions kept whole, the way word processors count them.
 */
const RE_COUNTED_WORD =
  /[\p{L}\p{N}][\p{L}\p{M}\p{N}]*(?:['’‐-][\p{L}\p{N}][\p{L}\p{M}\p{N}]*)*/gu;

const RE_CURLY_APOSTROPHE = /’/g;
const RE_POSSESSIVE_S = /'s$/;
const RE_HAS_HYPHEN = /[‐-]/;

export function normalizeWord(word: string): string {
  return word.toLowerCase().replace(RE_CURLY_APOSTROPHE, "'");
}

/** "society's" → "society". A contraction loses its tail too, which no list here needs. */
export function stripPossessive(lower: string): string {
  return lower.replace(RE_POSSESSIVE_S, "");
}

export function isHyphenated(word: string): boolean {
  return RE_HAS_HYPHEN.test(word);
}

function tokensOf(text: string, pattern: RegExp): Token[] {
  const tokens: Token[] = [];
  for (const match of text.matchAll(pattern)) {
    tokens.push({
      text: match[0],
      lower: normalizeWord(match[0]),
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return tokens;
}

/** Words of letters only, split at hyphens — for word lists and repetition. */
export function letterTokens(text: string): Token[] {
  return tokensOf(text, RE_LETTER_WORD);
}

/** Words as a word counter counts them, compounds whole — for phrase assembly. */
export function wordTokens(text: string): Token[] {
  return tokensOf(text, RE_COUNTED_WORD);
}

export function countWords(text: string): number {
  return text.match(RE_COUNTED_WORD)?.length ?? 0;
}

/** `count` per `base` words (100 or 1,000), or 0 for an empty text. */
export function ratePer(count: number, words: number, base: number): number {
  return words > 0 ? (count * base) / words : 0;
}

const RE_ONLY_WHITESPACE = /^\s+$/;

/** True when only spaces or line breaks separate the two tokens — no punctuation. */
export function adjacent(text: string, left: Token, right: Token): boolean {
  return RE_ONLY_WHITESPACE.test(text.slice(left.end, right.start));
}

/* ------------------------------------------------------------ capitalization */

const RE_UPPER_START = /^\p{Lu}/u;
const RE_AFTER_TERMINAL = /(?:[.!?…:;]["'”’)\]]*\s+|\n\s*)["'“‘([]*$/;
const RE_ONLY_OPENERS = /^\s*["'“‘([]*$/;

/** Whether a sentence (or a line) starts at `index`. */
export function isSentenceStart(text: string, index: number): boolean {
  const from = Math.max(0, index - 16);
  const before = text.slice(from, index);
  if (from === 0 && RE_ONLY_OPENERS.test(before)) {
    return true;
  }
  return RE_AFTER_TERMINAL.test(before);
}

/** Capitalized where no sentence starts — usually part of a name or a title. */
export function isCapitalizedMidSentence(text: string, start: number): boolean {
  return (
    RE_UPPER_START.test(text.slice(start, start + 1)) &&
    !isSentenceStart(text, start)
  );
}

/* ------------------------------------------------------------ quoted passages */

/**
 * Double-quoted passages (straight or curly) and single curly quotes. A single curly
 * closing mark is also an apostrophe, so inside a ‘…’ passage one followed by a letter
 * ("don’t") does not close it. Straight single quotes are too ambiguous to pair.
 */
const RE_QUOTED =
  /“[^“”]{0,1500}”|"[^"]{0,1500}"|‘(?:[^‘’]|’(?=\p{L})){0,1500}’(?!\p{L})/gu;

export function quotedSpans(text: string): Span[] {
  return [...text.matchAll(RE_QUOTED)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
}

export function insideAny(
  spans: readonly Span[],
  start: number,
  end: number
): boolean {
  return spans.some((span) => start >= span.start && end <= span.end);
}

/* ------------------------------------------------------------ phrase matching */

const RE_REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;
const RE_LITERAL_SPACE = / /g;
const RE_LITERAL_APOSTROPHE = /'/g;

function phraseSource(phrase: string): string {
  return phrase
    .replace(RE_REGEX_SPECIAL, "\\$&")
    .replace(RE_LITERAL_SPACE, "\\s+")
    .replace(RE_LITERAL_APOSTROPHE, "['’]");
}

/**
 * Any of `phrases` as whole words, case-insensitively, longest first so "a lot of" wins
 * over "a lot". A space in a phrase matches any run of whitespace, including a line
 * break; an apostrophe matches a straight or a curly one. A match never starts or ends
 * inside a word or a hyphenated compound, but a possessive may follow ("mankind's").
 */
export function phraseRegex(phrases: readonly string[]): RegExp {
  const alternation = [...phrases]
    .sort((a, b) => b.length - a.length)
    .map(phraseSource)
    .join("|");
  return new RegExp(
    `(?<![\\p{L}\\p{N}]|[\\p{L}\\p{N}]-)(?:${alternation})(?![\\p{L}\\p{N}]|-[\\p{L}\\p{N}])`,
    "giu"
  );
}

/** Earliest first; at the same start the longer match wins; overlaps are dropped. */
export function withoutOverlaps<T extends Span>(matches: readonly T[]): T[] {
  const ordered = [...matches].sort(
    (a, b) => a.start - b.start || b.end - a.end
  );
  const kept: T[] = [];
  let cursor = 0;
  for (const match of ordered) {
    if (match.start >= cursor) {
      kept.push(match);
      cursor = match.end;
    }
  }
  return kept;
}

/* ------------------------------------------------------------ sentences */

export interface Sentence extends Span {
  text: string;
  /** Ends with . ! ? or … — closing quotes and brackets may follow the mark. */
  terminated: boolean;
  /** The only sentence on its line or paragraph. Unterminated and short, it is a heading. */
  ownLine: boolean;
}

/** Never end a sentence: a name, a page or a citation always follows them. */
const HARD_ABBREVIATIONS = new Set([
  "assn",
  "capt",
  "cf",
  "col",
  "dept",
  "dr",
  "gen",
  "gov",
  "hon",
  "lt",
  "mr",
  "mrs",
  "ms",
  "mt",
  "mx",
  "prof",
  "rep",
  "rev",
  "sen",
  "sgt",
  "st",
  "univ",
  "viz",
  "vs",
]);

/**
 * End a sentence only when a capital letter follows: "Smith et al. (2019)" carries on,
 * "…and so on, etc. The next study" does not. A number after "p." or "Fig." continues.
 */
const SOFT_ABBREVIATIONS = new Set([
  "a.m",
  "al",
  "approx",
  "bros",
  "ca",
  "ch",
  "chap",
  "co",
  "corp",
  "ed",
  "eds",
  "eq",
  "eqs",
  "etc",
  "fig",
  "figs",
  "inc",
  "jr",
  "ltd",
  "no",
  "nos",
  "p",
  "p.m",
  "para",
  "pp",
  "sec",
  "sr",
  "trans",
  "vol",
  "vols",
]);

/** A run of end punctuation, closing marks after it, and the next visible character. */
const RE_TERMINAL_RUN = /([.!?…]+)["'”’)\]]*(?=\s+(\S)?)/g;
const RE_WORD_BEFORE_DOT = /(\p{L}[\p{L}.]*)$/u;
const RE_OPENS_SENTENCE = /[\p{Lu}\p{N}"“‘'([]/u;
const RE_UPPERCASE = /\p{Lu}/u;
const RE_ENDS_TERMINATED = /[.!?…]["'”’)\]]*$/;
const RE_LINE = /[^\n]+/g;
const RE_NEWLINE = /\n/g;
const RE_ENDS_CLAUSE = /[.!?…:;]["'”’)\]]*\s*$/;
const RE_STARTS_LOWERCASE = /^\s*\p{Ll}/u;
const RE_BLANK = /^\s*$/;
const RE_LEADING_SPACE = /^\s*/;
const RE_TRAILING_SPACE = /\s*$/;

/**
 * Lines that belong together. A line break starts a new segment unless the next line
 * carries on in lowercase from a line that did not end a clause — the hard-wrapped
 * paragraph a PDF pastes. A blank line always starts a new segment.
 */
function lineSegments(text: string): Span[] {
  const segments: Span[] = [];
  let open: Span | null = null;
  for (const match of text.matchAll(RE_LINE)) {
    const line = match[0];
    if (RE_BLANK.test(line)) {
      open = null;
      continue;
    }
    const start = match.index;
    const end = start + line.length;
    if (open && continuesLine(text, open, start, line)) {
      open.end = end;
      continue;
    }
    open = { start, end };
    segments.push(open);
  }
  return segments;
}

function continuesLine(
  text: string,
  open: Span,
  start: number,
  line: string
): boolean {
  const breaks = text.slice(open.end, start).match(RE_NEWLINE)?.length ?? 0;
  if (breaks > 1) {
    return false;
  }
  const tail = text.slice(Math.max(open.start, open.end - 8), open.end);
  return !RE_ENDS_CLAUSE.test(tail) && RE_STARTS_LOWERCASE.test(line);
}

function fullStopEnds(body: string, at: number, next: string): boolean {
  const before = body.slice(Math.max(0, at - 24), at);
  const word = RE_WORD_BEFORE_DOT.exec(before)?.[1] ?? "";
  const lower = word.toLowerCase();
  if (HARD_ABBREVIATIONS.has(lower)) {
    return false;
  }
  if (SOFT_ABBREVIATIONS.has(lower)) {
    return RE_UPPERCASE.test(next);
  }
  // An initial ("J. Smith") or an abbreviation with inner stops ("e.g.", "U.S.").
  if (word.length === 1 || lower.includes(".")) {
    return false;
  }
  return RE_OPENS_SENTENCE.test(next);
}

function endsSentence(
  body: string,
  at: number,
  marks: string,
  next: string | undefined
): boolean {
  if (next === undefined) {
    return true;
  }
  if (marks.includes("?") || marks.includes("!")) {
    return RE_OPENS_SENTENCE.test(next);
  }
  if (marks !== ".") {
    // An ellipsis ends a sentence only when the next one visibly starts.
    return RE_UPPERCASE.test(next);
  }
  return fullStopEnds(body, at, next);
}

function trimmed(body: string, piece: Span): Span {
  const slice = body.slice(piece.start, piece.end);
  const lead = RE_LEADING_SPACE.exec(slice)?.[0].length ?? 0;
  const trail = RE_TRAILING_SPACE.exec(slice)?.[0].length ?? 0;
  return {
    start: piece.start + lead,
    end: Math.max(piece.start + lead, piece.end - trail),
  };
}

function splitSegment(text: string, segment: Span): Sentence[] {
  const body = text.slice(segment.start, segment.end);
  const pieces: Span[] = [];
  let from = 0;
  for (const match of body.matchAll(RE_TERMINAL_RUN)) {
    const after = match.index + match[0].length;
    if (endsSentence(body, match.index, match[1], match[2])) {
      pieces.push({ start: from, end: after });
      from = after;
    }
  }
  pieces.push({ start: from, end: body.length });
  const real = pieces
    .map((piece) => trimmed(body, piece))
    .filter((piece) => piece.end > piece.start);
  return real.map((piece) => {
    const sentence = body.slice(piece.start, piece.end);
    return {
      start: segment.start + piece.start,
      end: segment.start + piece.end,
      text: sentence,
      terminated: RE_ENDS_TERMINATED.test(sentence),
      ownLine: real.length === 1,
    };
  });
}

/**
 * The text's sentences, with offsets.
 *
 * Splits at . ! ? and … when the next sentence visibly starts — a capital, a number or
 * an opening quote — and never after a title ("Dr."), an initial, "e.g." or "i.e.";
 * "et al.", "etc." and "p." end a sentence only before a capital. Line breaks split too,
 * so headings and list items come out as sentences of their own.
 */
export function splitSentences(text: string): Sentence[] {
  return lineSegments(text).flatMap((segment) => splitSegment(text, segment));
}

const HEADING_MAX_WORDS = 8;

/**
 * Per sentence: whether it is a heading — short, unterminated, alone on its line, and
 * followed by more text. The last line never counts, so a fragment typed to try a tool
 * ("make a decision") is still analysed.
 */
export function headingFlags(sentences: readonly Sentence[]): boolean[] {
  return sentences.map(
    (sentence, index) =>
      index < sentences.length - 1 &&
      !sentence.terminated &&
      sentence.ownLine &&
      countWords(sentence.text) <= HEADING_MAX_WORDS
  );
}

/** The index of the sentence containing `offset`, or -1. */
export function sentenceIndexAt(
  sentences: readonly Sentence[],
  offset: number
): number {
  let low = 0;
  let high = sentences.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const sentence = sentences[middle];
    if (offset < sentence.start) {
      high = middle - 1;
    } else if (offset >= sentence.end) {
      low = middle + 1;
    } else {
      return middle;
    }
  }
  return -1;
}

/* ------------------------------------------------------------ tallies */

export interface Tally {
  key: string;
  count: number;
}

/** Count occurrences per key, most frequent first, then alphabetically. */
export function tally(keys: Iterable<string>): Tally[] {
  const counts = new Map<string, number>();
  for (const key of keys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}
